import {
  MessageCircle,
  ClipboardList,
  Bot,
  BookOpen,
  Tags,
  GitFork,
  Clock,
  Shuffle,
  Flag,
  Tag,
  TagsIcon,
  UserCheck,
  UsersRound,
  PenLine,
  Globe,
  Briefcase,
  MoveRight,
  FileText,
  StickyNote,
  Activity,
  CheckCircle2,
  Gauge,
  Workflow,
} from 'lucide-react';

/**
 * O que a pessoa pode colocar no fluxo — em ordem de uso, não de implementação.
 *
 * Um bloco daqui vira um nó do ReactFlow: `tipo` é o `node.type` e `padroes`
 * entra em `node.data`. "Adicionar etiqueta" e "Passar para atendente" são o
 * mesmo `action` com `actionType` diferente — não tipos novos. Evita migration
 * no enum `flowBuilderNodeType` e mantém o espelhamento para as tabelas do
 * motor exatamente como está.
 *
 * `target` (Público-Alvo) NÃO está aqui de propósito: nunca existiu no motor,
 * era espelhado como mensagem vazia e derrubava a execução. Continua sendo
 * desenhado em fluxos antigos, com aviso no cartão e no painel de pendências.
 */

export type BlocoDaPaleta = {
  /** Chave única na paleta — `tipo` sozinho não serve, há três `action`. */
  chave: string;
  tipo: string;
  rotulo: string;
  descricao: string;
  icone: any;
  corDoIcone: string;
  padroes?: Record<string, any>;
};

export type GrupoDaPaleta = { nome: string; blocos: BlocoDaPaleta[] };

export const CATALOGO_DE_BLOCOS: GrupoDaPaleta[] = [
  {
    nome: 'Conversar',
    blocos: [
      {
        chave: 'mensagem',
        tipo: 'content',
        rotulo: 'Enviar mensagem',
        descricao: 'Texto, arquivo ou modelo aprovado, com botões de resposta',
        icone: MessageCircle,
        corDoIcone: 'text-green-600',
        padroes: { messageContent: { type: 'text', text: '', buttons: [] } },
      },
      {
        chave: 'pergunta',
        tipo: 'data_collection',
        rotulo: 'Fazer uma pergunta',
        descricao: 'Espera a resposta, valida e guarda num campo',
        icone: ClipboardList,
        corDoIcone: 'text-teal-600',
        padroes: { fieldType: 'text', maxRetries: 2 },
      },
      {
        chave: 'ia',
        tipo: 'ai_assistant',
        rotulo: 'Responder com IA',
        descricao: 'A IA conversa seguindo a sua instrução',
        icone: Bot,
        corDoIcone: 'text-indigo-600',
      },
      {
        chave: 'ia_base',
        tipo: 'ai_assistant',
        rotulo: 'Responder pela base',
        descricao: 'Só o que está na base de conhecimento, sem conversar',
        icone: BookOpen,
        corDoIcone: 'text-indigo-600',
        padroes: {
          modo: 'kb_only',
          useKnowledgeBase: true,
          systemPrompt: 'Responda dúvidas objetivas usando apenas a base de conhecimento da empresa.',
          fallbackMessage: 'Não tenho essa informação aqui. Vou te encaminhar para alguém da equipe.',
        },
      },
      {
        chave: 'classificador',
        tipo: 'ai_classifier',
        rotulo: 'Separar por assunto',
        descricao: 'A IA lê a mensagem e escolhe o caminho',
        icone: Tags,
        corDoIcone: 'text-cyan-600',
        padroes: { categories: ['', ''] },
      },
    ],
  },
  {
    nome: 'Organizar',
    blocos: [
      {
        chave: 'condicao',
        tipo: 'condition',
        rotulo: 'Dividir por condição',
        descricao: 'Segue caminhos diferentes: sim ou não',
        icone: GitFork,
        corDoIcone: 'text-yellow-600',
        padroes: { field: 'tag', operator: 'equals' },
      },
      {
        chave: 'esperar',
        tipo: 'delay',
        rotulo: 'Esperar',
        descricao: 'Pausa antes do próximo passo',
        icone: Clock,
        corDoIcone: 'text-purple-600',
        padroes: { delayMode: 'duration', duration: 5, unit: 'minutes' },
      },
      {
        chave: 'sorteio',
        tipo: 'randomizer',
        rotulo: 'Dividir o tráfego',
        descricao: 'Teste A/B: manda uma parte por cada caminho',
        icone: Shuffle,
        corDoIcone: 'text-orange-600',
        padroes: { path1Probability: 50 },
      },
      {
        chave: 'outro_fluxo',
        tipo: 'action',
        rotulo: 'Mandar para outro fluxo',
        descricao: 'Continua num fluxo reutilizável e volta a contar dali',
        icone: Workflow,
        corDoIcone: 'text-violet-600',
        padroes: { actionType: 'enroll_in_flow', onAlreadyEnrolled: 'skip' },
      },
      {
        chave: 'fim',
        tipo: 'end',
        rotulo: 'Fim do fluxo',
        descricao: 'Fecha um caminho que termina aqui',
        icone: Flag,
        corDoIcone: 'text-gray-500',
      },
    ],
  },
  {
    nome: 'Agir',
    blocos: [
      {
        chave: 'etiqueta',
        tipo: 'action',
        rotulo: 'Adicionar etiqueta',
        descricao: 'Marca o contato para filtrar e segmentar depois',
        icone: Tag,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'add-tag', tagIds: [] },
      },
      {
        chave: 'atendente',
        tipo: 'action',
        rotulo: 'Passar para atendente',
        descricao: 'Entrega a conversa a um vendedor e para o robô',
        icone: UserCheck,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'handoff', assignMode: 'auto', stopBot: true },
      },
      {
        chave: 'remover_etiqueta',
        tipo: 'action',
        rotulo: 'Remover etiqueta',
        descricao: 'Tira uma marcação que não vale mais',
        icone: TagsIcon,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'remove-tag', tagIds: [] },
      },
      {
        chave: 'equipe',
        tipo: 'action',
        rotulo: 'Passar para uma equipe',
        descricao: 'Entrega a quem estiver menos ocupado no time',
        icone: UsersRound,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'handoff', assignMode: 'team', teamUserIds: [], stopBot: true },
      },
      {
        chave: 'criar_deal',
        tipo: 'action',
        rotulo: 'Criar oportunidade',
        descricao: 'Abre o negócio no funil do CRM',
        icone: Briefcase,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'create_deal', seJaExistir: 'move' },
      },
      {
        chave: 'mover_etapa',
        tipo: 'action',
        rotulo: 'Mover etapa do funil',
        descricao: 'Avança a oportunidade que já existe',
        icone: MoveRight,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'move_deal_stage' },
      },
      {
        chave: 'proposta',
        tipo: 'action',
        rotulo: 'Enviar proposta',
        descricao: 'Manda o PDF da proposta aprovada do negócio',
        icone: FileText,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'send_proposal' },
      },
      {
        chave: 'pontuar',
        tipo: 'action',
        rotulo: 'Pontuar o contato',
        descricao: 'Soma ou tira pontos do lead scoring',
        icone: Gauge,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'update_score', points: 10, reason: 'flow_action' },
      },
      {
        chave: 'nota',
        tipo: 'action',
        rotulo: 'Nota interna',
        descricao: 'Deixa recado no Inbox sem o contato ver',
        icone: StickyNote,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'note' },
      },
      {
        chave: 'evento',
        tipo: 'action',
        rotulo: 'Registrar evento',
        descricao: 'Marca na linha do tempo do contato',
        icone: Activity,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'timeline_event' },
      },
      {
        chave: 'encerrar',
        tipo: 'action',
        rotulo: 'Encerrar com motivo',
        descricao: 'Fecha o atendimento classificado, para o relatório',
        icone: CheckCircle2,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'close_conversation', closeReason: 'resolved' },
      },
      {
        chave: 'campo',
        tipo: 'action',
        rotulo: 'Atualizar campo',
        descricao: 'Grava um valor no cadastro do contato',
        icone: PenLine,
        corDoIcone: 'text-emerald-600',
        padroes: { actionType: 'set-field' },
      },
      {
        chave: 'webhook',
        tipo: 'webhook_request',
        rotulo: 'Enviar para outro sistema',
        descricao: 'Manda os dados para uma URL (webhook)',
        icone: Globe,
        corDoIcone: 'text-rose-600',
        padroes: { method: 'POST' },
      },
    ],
  },
];

/**
 * Por qual saída o bloco continua quando ele é inserido no meio de uma
 * ligação. `null` = não tem saída (o fluxo termina nele).
 *
 * Cravar `'default'` para todos fazia o ReactFlow descartar a aresta no
 * desenho (não existe handle com esse id numa condição ou num randomizador),
 * enquanto ela continuava no estado, era salva e roteava no motor: a tela
 * mostrava um fluxo diferente do que rodava.
 */
export const SAIDA_PADRAO_POR_TIPO: Record<string, string | null> = {
  content: 'default',
  action: 'default',
  delay: 'default',
  webhook_request: 'default',
  target: 'default',
  condition: 'true',
  randomizer: 'path1',
  ai_assistant: 'success',
  data_collection: 'valid',
  // Classificador tem uma saída por categoria, definidas pelo usuário: não há
  // padrão possível antes de ele configurar.
  ai_classifier: null,
  end: null,
};

export const BLOCOS_POR_CHAVE = new Map(
  CATALOGO_DE_BLOCOS.flatMap((g) => g.blocos).map((b) => [b.chave, b]),
);
